import { useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchPlayoffBracket, fetchPlayoffSeasons } from '../services/league'
import { PlayoffBracket } from '../components/PlayoffBracket'
import { Card, EmptyState, Loading, Notice, Segmented } from '../components/ui'
import './Playoffs.css'

/**
 * The public view of a season's postseason.
 *
 * @remarks
 * `/playoffs` lands on the most recent season with a bracket;
 * `/playoffs/:seasonNumber` addresses one directly, which is the link the
 * season archive and the Hall of Champions use.
 */
export default function Playoffs() {
  const params = useParams<{ seasonNumber?: string }>()
  const navigate = useNavigate()
  const seasons = useAsync(fetchPlayoffSeasons, [])

  const requested = params.seasonNumber ? Number(params.seasonNumber) : null
  const available = seasons.data ?? []

  // Falling back to the newest bracket keeps the bare `/playoffs` link useful
  // without the caller having to know which seasons have one.
  const seasonNumber = requested ?? available[0]?.seasonNumber ?? null

  const bracket = useAsync(
    useMemo(
      () => () => (seasonNumber === null ? Promise.resolve(null) : fetchPlayoffBracket(seasonNumber)),
      [seasonNumber]
    ),
    [seasonNumber]
  )

  const summary = available.find(season => season.seasonNumber === seasonNumber)

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Playoffs</span>
          <h1>{seasonNumber === null ? 'Playoff brackets' : `Season ${seasonNumber} playoffs`}</h1>
          <p>
            {summary?.complete && summary.championName
              ? `${summary.championName} took the title.`
              : 'How the postseason was decided.'}
          </p>
        </div>

        {available.length > 1 && seasonNumber !== null && (
          <Segmented
            options={available.map(season => ({
              value: String(season.seasonNumber),
              label: `S${season.seasonNumber}`,
            }))}
            value={String(seasonNumber)}
            onChange={next => navigate(`/playoffs/${next}`)}
          />
        )}
      </header>

      {seasons.error && <Notice tone="error">{seasons.error}</Notice>}
      {bracket.error && <Notice tone="error">{bracket.error}</Notice>}

      {seasons.loading || bracket.loading ? (
        <Loading rows={6} />
      ) : bracket.data ? (
        <Card flush>
          <div className="playoffs__board">
            <PlayoffBracket bracket={bracket.data} />
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState title="No bracket for this season">
            Brackets are built in the Admin area. In the meantime, the{' '}
            <Link to="/hall-of-champions">Hall of Champions</Link> has every title holder.
          </EmptyState>
        </Card>
      )}

      {bracket.data && (
        <p className="playoffs__footnote">
          Regular-season standings exclude playoff games, so a bracket result never moves a
          conference table. Title holders are recorded in the{' '}
          <Link to="/hall-of-champions">Hall of Champions</Link>.
        </p>
      )}
    </div>
  )
}
